import type { WebhookEventSubscription } from '../typescript-types/webhook-event-subscriptions/webhook-event-subscription';

import { WebhookEventSubscriptionState } from './webhook-event-subscription-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';
import {
  Action,
  setStatusByParam,
  StateStatus,
  updateResourceByParam,
} from '../reducers/utils/state-manager';

import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: WebhookEventSubscriptionState = {
  webhookEventSubscriptionsById: {},
  webhookEventSubscriptionsStateByOrgId: {},
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_WEBHOOK_EVENT_SUBSCRIPTIONS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'webhookEventSubscriptions', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_WEBHOOK_EVENT_SUBSCRIPTIONS'): {
      const {
        args: { orgId },
        data: { webhookEventSubscriptions },
      } = action.payload;
      return updateResourceByParam(
        state,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        false,
        convertArrayToMap(webhookEventSubscriptions, (s: WebhookEventSubscription) =>
          s.webhookEventSubscriptionId.toString(),
        ),
      );
    }
    case FAILURE('LOAD_WEBHOOK_EVENT_SUBSCRIPTIONS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'webhookEventSubscriptions', 'orgId', orgId, false);
    }
    case STARTING('CREATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;

      return setStatusByParam(
        state,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        StateStatus.loading,
      );
    }
    case SUCCESS('CREATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { webhookEventSubscription } = action.payload.data;

      const newState = {
        ...state,
        webhookEventSubscriptionsById: {
          ...state.webhookEventSubscriptionsById,
          [webhookEventSubscription.webhookEventSubscriptionId]: webhookEventSubscription,
        },
      };
      return setStatusByParam(
        newState,
        'webhookEventSubscriptions',
        'orgId',
        webhookEventSubscription.orgId,
        StateStatus.success,
      );
    }
    case FAILURE('CREATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;

      return setStatusByParam(
        state,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        StateStatus.error,
      );
    }
    case STARTING('UPDATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;

      return setStatusByParam(
        state,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        StateStatus.loading,
      );
    }
    case SUCCESS('UPDATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const {
        args: { webhookEventSubscriptionId },
        data: { webhookEventSubscription },
      } = action.payload;

      const newState = {
        ...state,
        webhookEventSubscriptionsById: {
          ...state.webhookEventSubscriptionsById,
          [webhookEventSubscriptionId]: webhookEventSubscription,
        },
      };
      return setStatusByParam(
        newState,
        'webhookEventSubscriptions',
        'orgId',
        webhookEventSubscription.orgId,
        StateStatus.success,
      );
    }
    case FAILURE('UPDATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;

      return setStatusByParam(
        state,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        StateStatus.error,
      );
    }
    case STARTING('DELETE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;

      // In order to trigger reload when removing an item from state, we need to pretend we are re-loading, so set loading on STARTING.
      return updateResourceByParam(state, 'webhookEventSubscriptions', 'orgId', orgId, true);
    }
    case SUCCESS('DELETE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { webhookEventSubscriptionId, orgId } = action.payload.args;

      const newState = { ...state };
      if (newState.webhookEventSubscriptionsById[webhookEventSubscriptionId]) {
        delete newState.webhookEventSubscriptionsById[webhookEventSubscriptionId];
      }

      // Set state back to Success after removing the item. We insert an empty array since we don't need to change anything else.
      return updateResourceByParam(
        newState,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        false,
        [],
      );
    }
    case FAILURE('DELETE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;
      return setStatusByParam(
        state,
        'webhookEventSubscriptions',
        'orgId',
        orgId,
        StateStatus.success,
      );
    }
    default: {
      return state;
    }
  }
};
